'use client';

import {
  ControllerRenderProps,
  FieldValues,
  UseFormReturn,
} from 'react-hook-form';

import { Checkbox, CheckboxProps } from '@nextui-org/react';
import { z } from 'zod';

import {
  FormControl,
  FormField,
  FormItem,
  useFormField,
} from '@/components/ui/form';
import { Link } from '@/shared/navigation';
import { ROUTES } from '@/shared/routes/routes';

const CONSENT_ERROR = 'Необходимо дать согласие на обработку персональных данных';

/**
 * Схема поля-галочки согласия. Подключается в zod-схему формы как
 * `personalDataConsent: personalDataConsentSchema`.
 */
export const personalDataConsentSchema = z
  .boolean({ required_error: CONSENT_ERROR })
  .refine((value) => value === true, { message: CONSENT_ERROR });

interface Props extends Omit<CheckboxProps, 'children'> {
  formReactHookForm: UseFormReturn<any>;
  name: string;
}

/** Галочка согласия на обработку персональных данных со ссылками на документы. */
export const FormConsentCheckbox = ({ formReactHookForm, ...props }: Props) => {
  return (
    <FormField
      control={formReactHookForm.control}
      name={props.name}
      render={({ field }) => (
        <FormItemConsentCheckbox
          {...props}
          {...field}
          // RHF отдаёт undefined до первого клика — NextUI нужен boolean.
          value={undefined}
          isSelected={field.value === true}
          onValueChange={field.onChange}
        />
      )}
    />
  );
};

const FormItemConsentCheckbox = (
  props: Omit<Props, 'formReactHookForm'> &
    Omit<ControllerRenderProps<FieldValues, string>, 'value' | 'onChange'>,
) => {
  const { error } = useFormField();
  return (
    <FormItem>
      <FormControl>
        <Checkbox
          size="sm"
          {...props}
          isInvalid={!!error?.message}
          color={error?.message ? 'danger' : (props.color ?? 'primary')}
        >
          <span className="text-xs leading-snug text-default-600">
            Я даю{' '}
            <Link
              className="underline"
              href={ROUTES.consent}
              target="_blank"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              согласие на обработку персональных данных
            </Link>{' '}
            и соглашаюсь с{' '}
            <Link
              className="underline"
              href={ROUTES.privacy}
              target="_blank"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              политикой конфиденциальности
            </Link>
          </span>
        </Checkbox>
      </FormControl>
      {error?.message && (
        <p className="text-tiny text-danger mt-1">{error.message}</p>
      )}
    </FormItem>
  );
};
